import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Copy, CheckCircle, Loader2, Briefcase } from 'lucide-react';
import { logger } from '../utils/logger';
import { API } from '../utils/apiBase';
import { useFeatureGate } from '../utils/useFeatureGate';


export default function CoverLetterGenerator() {
  const navigate = useNavigate();
  const apiRef = useRef(API);
  const { checkGate, gateModal } = useFeatureGate();
  const [resumes, setResumes] = useState([]);
  const [selectedResume, setSelectedResume] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [coverLetter, setCoverLetter] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchResumes = async () => {
      try {
        const res = await axios.get(`${apiRef.current}/resumes`, { withCredentials: true });
        setResumes(res.data);
        if (res.data.length > 0) setSelectedResume(res.data[0].resume_id);
      } catch (err) {
        logger.error('Failed to fetch resumes:', err);
      }
    };
    fetchResumes();
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!selectedResume || !jobDescription.trim()) return;
    if (await checkGate('cover_letter')) return;
    setLoading(true);
    setError('');
    setCoverLetter(null);
    try {
      const res = await axios.post(`${apiRef.current}/cover-letter/generate`, {
        resume_id: selectedResume,
        job_description: jobDescription,
        company_name: companyName || undefined,
      }, { withCredentials: true });
      setCoverLetter(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate cover letter.');
      logger.error('Cover letter failed:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedResume, jobDescription, companyName, checkGate]);

  const handleCopy = useCallback(() => {
    if (coverLetter?.cover_letter) {
      navigator.clipboard.writeText(coverLetter.cover_letter);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [coverLetter]);

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20" data-testid="cover-letter-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/dashboard')} className="inline-flex items-center space-x-2 text-[#4b5563] hover:text-[#002fa7] mb-4" data-testid="back-button">
          <ArrowLeft size={20} /><span>Back to Dashboard</span>
        </button>
        <h1 className="font-satoshi text-3xl font-black tracking-tight text-[#0f1115] mb-1">Cover Letter Generator</h1>
        <p className="text-[#4b5563] mb-8">AI-powered cover letters tailored to the job description</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input */}
          <div className="space-y-4">
            <div className="dashboard-card p-6">
              <label className="block text-sm font-semibold text-[#0f1115] mb-2">
                <FileText size={16} className="inline mr-1 text-[#002fa7]" /> Select Resume *
              </label>
              <select
                value={selectedResume}
                onChange={(e) => setSelectedResume(e.target.value)}
                className="w-full px-4 py-3 border border-[#e5e7eb] bg-white text-sm focus:border-[#002fa7] transition-colors"
                data-testid="resume-select"
              >
                {resumes.map((r) => (
                  <option key={r.resume_id} value={r.resume_id}>{r.title} (Score: {r.ats_score})</option>
                ))}
              </select>
            </div>

            <div className="dashboard-card p-6">
              <label className="block text-sm font-semibold text-[#0f1115] mb-2">
                <Briefcase size={16} className="inline mr-1 text-[#002fa7]" /> Company Name (optional)
              </label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="e.g., Google"
                className="w-full px-4 py-3 border border-[#e5e7eb] text-sm focus:border-[#002fa7] transition-colors"
                data-testid="company-input"
              />
            </div>

            <div className="dashboard-card p-6">
              <label className="block text-sm font-semibold text-[#0f1115] mb-2">
                <Briefcase size={16} className="inline mr-1 text-[#002fa7]" /> Job Description *
              </label>
              <textarea
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                rows={10}
                placeholder="Paste the full job description here..."
                className="w-full px-4 py-3 border border-[#e5e7eb] text-sm focus:border-[#002fa7] transition-colors resize-none"
                data-testid="jd-input"
              />
            </div>

            <button
              onClick={handleGenerate}
              disabled={loading || !selectedResume || !jobDescription.trim()}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="generate-btn"
            >
              {loading ? <><Loader2 size={18} className="animate-spin" /> Generating...</> : 'Generate Cover Letter'}
            </button>
            {error && <p className="text-sm text-[#e11d48]" data-testid="error-msg">{error}</p>}
          </div>

          {/* Output */}
          <div>
            {coverLetter ? (
              <div className="dashboard-card p-6" data-testid="cover-letter-result">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-satoshi text-lg font-bold text-[#0f1115]">Your Cover Letter</h2>
                  <button onClick={handleCopy} className="btn-outline text-sm inline-flex items-center gap-1" data-testid="copy-btn">
                    {copied ? <><CheckCircle size={14} className="text-[#10b981]" /> Copied!</> : <><Copy size={14} /> Copy</>}
                  </button>
                </div>
                <div className="prose prose-sm text-[#4b5563] whitespace-pre-wrap leading-relaxed" data-testid="cover-letter-text">
                  {coverLetter.cover_letter}
                </div>
              </div>
            ) : (
              <div className="dashboard-card p-12 text-center text-[#6b7280]">
                <FileText size={48} className="mx-auto mb-4 text-[#d1d5db]" />
                <p className="font-semibold">Your cover letter will appear here</p>
                <p className="text-sm mt-1">Select a resume and paste a job description to get started</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {gateModal}
    </div>
  );
}
